import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { motion } from "framer-motion";
import { FiLock, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import Input from "../../../components/input";
import { baseUrlNoAuth } from "../../../hooks/remote/apiClient";
import { showSuccessMessage, showErrorMessage } from "../../../hooks/constants";

const AcceptInvitation = () => {
  document.title = "Accept Invitation - CloudVantage";

  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null); 
  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  const form = useFormik({
    initialValues: {
      password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Password is required"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords do not match")
        .required("Please confirm your password"),
    }),
    onSubmit: async (values, { resetForm }) => {
      if (!token) {
        showErrorMessage("Invitation token is missing. Please use the link from your email.");
        setStatus("error");
        setStatusMessage("Invitation token is missing. Please use the link from your email.");
        return;
      }

      setLoading(true);
      setStatus(null);

      try {
        const response = await baseUrlNoAuth.post("school/accept-invitation/", {
          token,
          password: values.password,
        });

        const data = response.data;

        // Handle both statuscode-based and generic success responses
        const isSuccess =
          data?.statuscode === "00" ||
          response.status === 200 ||
          response.status === 201;

        const message =
          data?.statusmessage ||
          data?.message ||
          "Your password has been set successfully! You can now sign in.";

        if (isSuccess) {
          setStatus("success");
          setStatusMessage(message);
          showSuccessMessage(message);
          resetForm();
          // Redirect to sign-in after a short delay so user can read the message
          setTimeout(() => navigate("/"), 3000);
        } else {
          const errMsg = data?.statusmessage || data?.message || "Something went wrong. Please try again.";
          setStatus("error");
          setStatusMessage(errMsg);
          showErrorMessage(errMsg);
        }
      } catch (err) {
        const errMsg =
          err?.response?.data?.statusmessage ||
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          "Failed to accept invitation. The link may have expired or already been used.";
        setStatus("error");
        setStatusMessage(errMsg);
        showErrorMessage(errMsg);
      } finally {
        setLoading(false);
      }
    },
  });

  // ─── No token in URL ────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="w-full text-center">
        <div className="flex justify-center mb-4">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <FiAlertCircle className="text-red-500 text-3xl" />
          </span>
        </div>
        <h2 className="text-2xl font-bold dark:text-white mb-2">Invalid Invitation Link</h2>
        <p className="text-gray-400 text-sm mb-6">
          This invitation link is missing a token. Please use the exact link sent to your email.
        </p>
        <button
          onClick={() => navigate("/")}
          className="text-primaryBlue text-sm font-semibold hover:underline"
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  // ─── Success state ───────────────────────────────────────────────────────────
  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="w-full text-center"
      >
        <div className="flex justify-center mb-6">
          <span className="w-20 h-20 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <FiCheckCircle className="text-green-500 text-4xl" />
          </span>
        </div>
        <h2 className="text-2xl font-bold dark:text-white mb-2">You're all set! 🎉</h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">{statusMessage}</p>
        <p className="text-gray-400 text-xs mb-8">Redirecting you to sign in…</p>
        <button
          onClick={() => navigate("/")}
          className="bg-primaryBlue text-white px-6 py-3 rounded-lg text-sm font-semibold shadow-lg shadow-blue-200 dark:shadow-none hover:opacity-90 transition-opacity"
        >
          Sign In Now
        </button>
      </motion.div>
    );
  }

  // ─── Main form ───────────────────────────────────────────────────────────────
  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8 text-center md:text-left">
        <div className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 text-primaryBlue text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
          <span className="w-1.5 h-1.5 rounded-full bg-primaryBlue animate-pulse" />
          Student Invitation
        </div>
        <h2 className="text-2xl font-bold dark:text-white mb-2">Set Your Password</h2>
        <p className="text-gray-400 text-sm leading-relaxed">
          Welcome to CloudVantage Schools Portal! Create a secure password to activate your student account.
        </p>
      </div>

      {/* Error banner */}
      {status === "error" && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg p-4 mb-6"
        >
          <FiAlertCircle className="text-red-500 text-lg shrink-0 mt-0.5" />
          <p className="text-red-600 dark:text-red-400 text-sm">{statusMessage}</p>
        </motion.div>
      )}

      {/* Form */}
      <form onSubmit={form.handleSubmit} className="grid gap-5">
        <Input
          label="New Password"
          type="password"
          isPassword="true"
          name="password"
          placeholder="············"
          prefixIcon={FiLock}
          value={form.values.password}
          onBlur={form.handleBlur}
          onChange={form.handleChange}
          onError={form.touched.password && form.errors.password}
        />

        <Input
          label="Confirm Password"
          type="password"
          isPassword="true"
          name="confirm_password"
          placeholder="············"
          prefixIcon={FiLock}
          value={form.values.confirm_password}
          onBlur={form.handleBlur}
          onChange={form.handleChange}
          onError={form.touched.confirm_password && form.errors.confirm_password}
        />

        {/* Password hints */}
        <ul className="text-xs text-gray-400 dark:text-gray-500 space-y-1 -mt-1 pl-1">
          <li className={`flex items-center gap-1.5 transition-colors ${form.values.password.length >= 8 ? "text-green-500" : ""}`}>
            <span className={`w-1 h-1 rounded-full ${form.values.password.length >= 8 ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"}`} />
            At least 8 characters
          </li>
        </ul>

        <div className="mt-2">
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primaryBlue text-white py-4 text-base font-semibold rounded-lg shadow-lg shadow-blue-200 dark:shadow-none transition-all active:scale-[0.98] hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Activating Account…
              </>
            ) : (
              "Activate My Account"
            )}
          </button>
        </div>
      </form>

      <p className="text-center text-xs text-gray-400 mt-6">
        Already have an account?{" "}
        <button
          type="button"
          onClick={() => navigate("/")}
          className="text-primaryBlue font-semibold hover:underline"
        >
          Sign In
        </button>
      </p>
    </div>
  );
};

export default AcceptInvitation;
